import React, { useState, useEffect } from 'react';
import ClaimRealityCard from './ClaimRealityCard';
import ClaudeAnalysisForm from './ClaudeAnalysisForm';
import Modal from './Modal';
import { Search, Filter, CheckSquare, Square } from 'lucide-react';

const STATUS_OPTIONS = {
  New: 'New',
  Processing: 'Processing',
  Posted: 'Posted',
  Discarded: 'Discarded'
};

const JOB_STATUS = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed'
};

const PolitiFactScraper = () => {
  const [url, setUrl] = useState('');
  const [maxQuotes, setMaxQuotes] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [status, setStatus] = useState(STATUS_OPTIONS.New);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showClaudeForm, setShowClaudeForm] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // New state for job-based approach
  const [activeJob, setActiveJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [speakerFilter, setSpeakerFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Batch selection state
  const [selectedQuoteIds, setSelectedQuoteIds] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  
  // Tags state for quote form
  const [existingTags, setExistingTags] = useState([]);

  // Load quotes based on current status filter, page, and search filters
  useEffect(() => {
    fetchQuotes();
  }, [status, currentPage]);
  
  // Fetch all tags when component mounts
  useEffect(() => {
    fetchTags();
  }, []);
  
  // We're using direct processing without job polling
  useEffect(() => {
    // Clean up any existing polling intervals when the component unmounts
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Fetch existing tags from the system
  const fetchTags = async () => {
    try {
      const response = await fetch('/api/admin/tags');
      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }
      const data = await response.json();
      setExistingTags(data.tags);
    } catch (error) {
      console.error("Error fetching tags:", error);
      // We don't set an error message to the user since tags are optional
    }
  };

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      // Build query params including search and filters
      const params = new URLSearchParams({
        status: status,
        page: currentPage,
        limit: 10
      });
      
      // Add optional filters if set
      if (searchTerm) params.append('search', searchTerm);
      if (speakerFilter) params.append('speaker', speakerFilter);
      if (ratingFilter) params.append('rating', ratingFilter);
      if (dateFilter) params.append('date', dateFilter);
      
      // Use new quotes endpoint from job-based API
      const response = await fetch(`/api/scraper/quotes?${params.toString()}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setQuotes(data.quotes);
        setTotalPages(data.totalPages);
        
        // Reset selection state when quotes change
        setSelectedQuoteIds({});
        setSelectAll(false);
      } else {
        setError(data.error || 'Failed to fetch quotes');
      }
    } catch (error) {
      setError(error.message || 'An error occurred while fetching quotes');
    } finally {
      setLoading(false);
    }
  };

  const createScrapeJob = async () => {
    if (!url) {
      setError('Please enter a PolitiFact URL');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setImportResults(null);
      
      // Use the enhanced batch save endpoint directly
      console.log(`Using enhanced batch save with URL: ${url} and max quotes: ${maxQuotes}`);
      
      // Call the enhanced batch save endpoint directly
      const batchSaveUrl = `/api/scraper/enhanced-batch-save?url=${encodeURIComponent(url)}&maxQuotes=${maxQuotes}`;
      
      // Show processing state
      setActiveJob('direct-batch');
      setJobStatus('processing');
      
      // Make the request
      const batchResponse = await fetch(batchSaveUrl);
      
      if (!batchResponse.ok) {
        const errorText = await batchResponse.text();
        throw new Error(`Batch processing error (${batchResponse.status}): ${errorText}`);
      }
      
      const batchResults = await batchResponse.json();
      
      if (batchResults.success) {
        // Update the job status
        setJobStatus('completed');
        
        // Set the import results
        setImportResults({
          newQuotes: batchResults.results.saved,
          duplicates: batchResults.results.duplicates,
          message: batchResults.message
        });
        
        // Refresh the quotes list
        fetchQuotes();
      } else {
        setJobStatus('failed');
        setError(batchResults.error || 'Failed to process quotes');
      }
    } catch (error) {
      console.error("Error processing quotes:", error);
      setJobStatus('failed');
      setError(error.message || 'An error occurred while processing quotes');
    } finally {
      setLoading(false);
    }
  };
  
  const startJobProcessing = async (jobId) => {
    try {
      setLoading(true);
      setJobStatus('processing');
      
      // Start processing the job
      const apiUrl = `/api/scraper/process-job?jobId=${jobId}`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setJobStatus('completed');
        setImportResults({
          newQuotes: data.results.newQuotes,
          duplicates: data.results.duplicates,
          message: data.message
        });
        // Refresh the quotes list
        fetchQuotes();
      } else {
        setJobStatus('failed');
        setError(data.error || 'Failed to process job');
      }
    } catch (error) {
      console.error("Error processing job:", error);
      setJobStatus('failed');
      setError(error.message || 'An error occurred while processing job');
    } finally {
      setLoading(false);
    }
  };
  
  // We no longer need the checkJobStatus function since we're using direct processing

  const handleUpdateStatus = async (id, newStatus, postedId = null) => {
    try {
      // Use URL parameters for quote status update
      const searchParams = new URLSearchParams();
      searchParams.append('id', id);
      searchParams.append('status', newStatus);
      
      if (postedId) {
        searchParams.append('postedId', postedId);
      }
      
      console.log("Updating quote status:", searchParams.toString());
      
      // Use the new update-quote endpoint
      const apiUrl = `/api/scraper/update-quote?${searchParams.toString()}`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update the local state
        setQuotes(quotes.map(quote => 
          quote.id === id ? { ...quote, status: newStatus } : quote
        ));
        
        // If the quote is now in a different category than the current filter, it should disappear
        if (newStatus !== status) {
          setQuotes(quotes.filter(quote => quote.id !== id));
        }
      } else {
        setError(data.error || 'Failed to update quote status');
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setError(error.message || 'An error occurred while updating quote status');
    }
  };

  const handleSendToClaude = (quote) => {
    setSelectedQuote(quote);
    setShowClaudeForm(true);
    handleUpdateStatus(quote.id, STATUS_OPTIONS.Processing);
  };

  const handlePreview = (quote) => {
    setSelectedQuote(quote);
    setShowPreview(true);
  };

  const handleCloseModal = () => {
    setShowPreview(false);
    setShowClaudeForm(false);
    setSelectedQuote(null);
  };

  const handleClaudeAnalysisComplete = (result) => {
    if (result && result.quoteId) {
      handleUpdateStatus(selectedQuote.id, STATUS_OPTIONS.Posted, result.quoteId);
      handleCloseModal();
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return 'Unknown date';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      
      // Format manually without date-fns
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    } catch (error) {
      return dateStr || 'Unknown date';
    }
  };
  
  // Toggle selection of a single quote
  const toggleQuoteSelection = (quoteId) => {
    setSelectedQuoteIds(prev => ({
      ...prev,
      [quoteId]: !prev[quoteId]
    }));
  };
  
  // Toggle select all quotes on current page
  const toggleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedQuoteIds({});
    } else {
      // Select all on current page
      const newSelected = {};
      quotes.forEach(quote => {
        newSelected[quote.id] = true;
      });
      setSelectedQuoteIds(newSelected);
    }
    setSelectAll(!selectAll);
  };
  
  // Count selected quotes
  const countSelectedQuotes = () => {
    return Object.values(selectedQuoteIds).filter(v => v).length;
  };
  
  // Process batch of quotes with Claude using new batch endpoint
  const processBatchWithClaude = async () => {
    const selectedIds = Object.entries(selectedQuoteIds)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => parseInt(id, 10));
      
    if (selectedIds.length === 0) {
      setError('No quotes selected for batch processing');
      return;
    }
    
    if (!confirm(`Send ${selectedIds.length} quote(s) to Claude for analysis?`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Use batch update endpoint to mark all selected quotes as Processing
      const response = await fetch('/api/scraper/batch-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quoteIds: selectedIds,
          status: STATUS_OPTIONS.Processing
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }
      
      const result = await response.json();
      
      alert(`Status of ${result.updated} quote(s) updated to Processing. They are now ready for individual analysis.`);
      
      // Refresh the quotes list
      await fetchQuotes();
    } catch (error) {
      console.error("Error in batch processing:", error);
      setError(error.message || 'An error occurred during batch processing');
    } finally {
      setLoading(false);
    }
  };
  
  // Batch discard selected quotes using new batch endpoint
  const batchDiscardQuotes = async () => {
    const selectedIds = Object.entries(selectedQuoteIds)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => parseInt(id, 10));
      
    if (selectedIds.length === 0) {
      setError('No quotes selected for batch discard');
      return;
    }
    
    if (!confirm(`Discard ${selectedIds.length} selected quote(s)? This action cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Use batch update endpoint to mark all selected quotes as Discarded
      const response = await fetch('/api/scraper/batch-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quoteIds: selectedIds,
          status: STATUS_OPTIONS.Discarded
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }
      
      const result = await response.json();
      
      alert(`${result.updated} quote(s) have been discarded.`);
      
      // Refresh the quotes list
      await fetchQuotes();
    } catch (error) {
      console.error("Error in batch discard:", error);
      setError(error.message || 'An error occurred during batch discard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Quote Queue Management</h1>
        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-800">
          <h2 className="font-medium text-lg mb-2">Quote Processing Workflow</h2>
          <ol className="list-decimal pl-5 space-y-1">
            <li><span className="font-medium">Import Quotes</span>: Use the scraper to extract quotes from PolitiFact (they start with <span className="px-2 py-0.5 bg-blue-100 rounded-full text-xs font-medium">New</span> status)</li>
            <li><span className="font-medium">Analyze</span>: Send quotes for Claude analysis (puts them in <span className="px-2 py-0.5 bg-yellow-100 rounded-full text-xs font-medium">Processing</span> status)</li>
            <li><span className="font-medium">Review and Edit</span>: Make final adjustments to Claude's analysis</li>
            <li><span className="font-medium">Publish</span>: After review, quotes become <span className="px-2 py-0.5 bg-green-100 rounded-full text-xs font-medium">Posted</span> and visible on the public site</li>
          </ol>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter PolitiFact URL (list page, speaker page, or individual quote)"
            className="flex-1 p-2 border rounded"
            disabled={activeJob && (jobStatus === 'pending' || jobStatus === 'processing')}
          />
          <div className="flex gap-2 items-center">
            <div className="flex items-center">
              <label htmlFor="maxQuotes" className="mr-2 text-sm whitespace-nowrap">Max quotes:</label>
              <select
                id="maxQuotes"
                value={maxQuotes}
                onChange={(e) => setMaxQuotes(e.target.value)}
                className="p-2 border rounded"
                disabled={activeJob && (jobStatus === 'pending' || jobStatus === 'processing')}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="0">Unlimited</option>
              </select>
            </div>
            <button
              onClick={createScrapeJob}
              disabled={loading || (activeJob && (jobStatus === 'pending' || jobStatus === 'processing'))}
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating Job...' : 'Scan for Quotes'}
            </button>
          </div>
        </div>
        
        {/* Job Status Display */}
        {activeJob && (
          <div className={`mb-4 p-4 rounded border ${
            jobStatus === 'completed' ? 'bg-green-50 border-green-200' :
            jobStatus === 'failed' ? 'bg-red-50 border-red-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center mb-2">
              <span className="font-semibold mr-2">Job Status:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                jobStatus === 'completed' ? 'bg-green-100 text-green-800' :
                jobStatus === 'failed' ? 'bg-red-100 text-red-800' :
                jobStatus === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {JOB_STATUS[jobStatus] || 'Unknown'}
              </span>
            </div>
            
            <div className="text-sm">
              <p>Job ID: <span className="font-mono">{activeJob}</span></p>
              {jobStatus === 'pending' && <p>Waiting to start processing...</p>}
              {jobStatus === 'processing' && (
                <div className="flex items-center mt-2">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <p>Processing job, please wait...</p>
                </div>
              )}
            </div>
            
            {jobStatus === 'failed' && (
              <div className="mt-2 text-red-700">
                <p>An error occurred while processing this job.</p>
              </div>
            )}
            
            {(jobStatus === 'completed' || jobStatus === 'failed') && (
              <button 
                onClick={() => {
                  setActiveJob(null);
                  setJobStatus(null);
                }} 
                className="mt-2 text-sm px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
              >
                Clear Job
              </button>
            )}
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button 
              onClick={() => setError(null)} 
              className="ml-4 text-sm px-2 py-1 bg-red-200 rounded hover:bg-red-300"
            >
              Dismiss
            </button>
          </div>
        )}
        
        {importResults && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <p>{importResults.message}</p>
            <p>New quotes added: {importResults.newQuotes}</p>
            <p>Duplicates found: {importResults.duplicates}</p>
          </div>
        )}
        
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4 text-sm">
          <p className="font-semibold mb-1">Enhanced Direct Extraction Method</p>
          <p>This version uses direct HTTP requests with improved extraction patterns to reliably get content from PolitiFact.</p>
          <p>Try these example URLs:</p>
          <ul className="list-disc pl-5 mt-1">
            <li>List page: <code className="bg-blue-100 px-1">https://www.politifact.com/factchecks/list/</code></li>
            <li>Speaker page: <code className="bg-blue-100 px-1">https://www.politifact.com/personalities/donald-trump/</code></li>
            <li>
              Individual quote: <code className="bg-blue-100 px-1">https://www.politifact.com/factchecks/2025/mar/05/donald-trump/fact-checking-trumps-claim-that-a-group-headed-up/</code>
            </li>
          </ul>
        </div>
        
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4">
          <p className="font-semibold mb-1">Extraction Tools</p>
          <p>You can test URL extraction with these tools:</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button 
              onClick={() => window.open(`/api/scraper/test-direct-extraction?url=${encodeURIComponent(url)}`, '_blank')}
              className="bg-indigo-600 text-white px-3 py-1 rounded"
              disabled={!url}
            >
              Test Extraction Patterns
            </button>
            <button 
              onClick={() => window.open(`/api/scraper/enhanced-direct-save?url=${encodeURIComponent(url)}`, '_blank')}
              className="bg-green-600 text-white px-3 py-1 rounded"
              disabled={!url}
            >
              Enhanced Direct Save
            </button>
            <button 
              onClick={() => window.open(`/api/scraper/experimental-extract?url=${encodeURIComponent(url)}`, '_blank')}
              className="bg-purple-600 text-white px-3 py-1 rounded"
              disabled={!url}
            >
              Detailed Extraction Analysis
            </button>
            <button 
              onClick={() => window.open(`/api/scraper/debug-link-extraction?url=${encodeURIComponent(url)}`, '_blank')}
              className="bg-indigo-600 text-white px-3 py-1 rounded"
              disabled={!url}
            >
              Debug Link Extraction
            </button>
            <button 
              onClick={() => window.open(`/api/scraper/enhanced-batch-save?url=${encodeURIComponent(url)}&maxQuotes=3`, '_blank')}
              className="bg-blue-600 text-white px-3 py-1 rounded"
              disabled={!url}
            >
              Test Batch Processing (3 quotes)
            </button>
            <button 
              onClick={() => window.open(`/api/scraper/check-quote-hashes`, '_blank')}
              className="bg-amber-600 text-white px-3 py-1 rounded"
            >
              Check Quote Hashes
            </button>
          </div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded mb-4">
          <p className="font-semibold mb-1">Admin Tools</p>
          <p>Reset hash values to allow reimporting of previously imported quotes:</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button 
              onClick={async () => {
                try {
                  setLoading(true);
                  const response = await fetch('/api/scraper/create-imported-quotes-table');
                  
                  if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                  }
                  
                  const result = await response.json();
                  alert(`Success: ${result.message}`);
                } catch (error) {
                  console.error('Error creating table:', error);
                  alert(`Error: ${error.message}`);
                } finally {
                  setLoading(false);
                }
              }}
              className="bg-green-600 text-white px-3 py-1 rounded"
            >
              Create Table
            </button>
            
            <button 
              onClick={async () => {
                if (confirm('DELETE ALL IMPORTED QUOTES? This will completely remove all imported quotes from the database so they can be reimported. This action cannot be undone.')) {
                  try {
                    setLoading(true);
                    const response = await fetch('/api/admin/clear-quote-hash', {
                      method: 'POST'
                    });
                    
                    if (!response.ok) {
                      throw new Error(`Request failed with status ${response.status}`);
                    }
                    
                    const result = await response.json();
                    alert(`Success: ${result.message}`);
                    // Refresh quotes list after deletion
                    fetchQuotes();
                  } catch (error) {
                    console.error('Error deleting quotes:', error);
                    alert(`Error: ${error.message}`);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              className="bg-red-600 text-white px-3 py-1 rounded"
            >
              Delete All Imported Quotes
            </button>
            
            <button 
              onClick={async () => {
                const selectedIds = Object.entries(selectedQuoteIds)
                  .filter(([_, isSelected]) => isSelected)
                  .map(([id, _]) => parseInt(id, 10));
                  
                if (selectedIds.length === 0) {
                  alert('Please select quotes first');
                  return;
                }
                
                if (confirm(`DELETE ${selectedIds.length} selected quotes? This will completely remove them from the database so they can be reimported. This action cannot be undone.`)) {
                  try {
                    setLoading(true);
                    const response = await fetch('/api/admin/clear-quote-hash', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        quoteIds: selectedIds
                      })
                    });
                    
                    if (!response.ok) {
                      throw new Error(`Request failed with status ${response.status}`);
                    }
                    
                    const result = await response.json();
                    alert(`Success: ${result.message}`);
                    // Reset selection state since the quotes have been deleted
                    setSelectedQuoteIds({});
                    setSelectAll(false);
                    // Refresh quotes list after deletion
                    fetchQuotes();
                  } catch (error) {
                    console.error('Error deleting quotes:', error);
                    alert(`Error: ${error.message}`);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              className="bg-red-500 text-white px-3 py-1 rounded"
              disabled={countSelectedQuotes() === 0}
            >
              Delete Selected Quotes
            </button>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.values(STATUS_OPTIONS).map((option) => (
            <button
              key={option}
              onClick={() => { setStatus(option); setCurrentPage(1); }}
              className={`px-4 py-2 rounded ${
                status === option
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
        
        {/* Search and filters */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            {/* Search */}
            <div className="flex flex-grow items-center border rounded-md overflow-hidden bg-white">
              <div className="pl-3 pr-2">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input 
                type="text"
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-2 px-2 w-full border-none focus:ring-0 focus:outline-none"
              />
              {searchTerm && (
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    fetchQuotes();
                  }}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              )}
            </div>
            
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            
            <button
              onClick={fetchQuotes}
              className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
          
          {/* Advanced filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Speaker</label>
                <input
                  type="text"
                  placeholder="Filter by speaker..."
                  value={speakerFilter}
                  onChange={(e) => setSpeakerFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <select
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All Ratings</option>
                  <option value="True">True</option>
                  <option value="Mostly True">Mostly True</option>
                  <option value="Half True">Half True</option>
                  <option value="Mostly False">Mostly False</option>
                  <option value="False">False</option>
                  <option value="Pants on Fire">Pants on Fire</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Batch action buttons */}
        {countSelectedQuotes() > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex flex-wrap items-center justify-between">
              <div className="text-blue-700 font-medium">
                {countSelectedQuotes()} quote{countSelectedQuotes() !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-2">
                {status === STATUS_OPTIONS.New && (
                  <button
                    onClick={processBatchWithClaude}
                    className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                  >
                    Send Selected to Claude
                  </button>
                )}
                <button
                  onClick={batchDiscardQuotes}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
                >
                  Discard Selected
                </button>
              </div>
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center my-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No quotes found with status: {status}
            {searchTerm && <div className="mt-2">Search term: {searchTerm}</div>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <button
                          onClick={toggleSelectAll}
                          className="focus:outline-none"
                        >
                          {selectAll ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Speaker
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quote
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quotes.map((quote) => (
                    <tr key={quote.id}>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <button
                            onClick={() => toggleQuoteSelection(quote.id)}
                            className="focus:outline-none"
                          >
                            {selectedQuoteIds[quote.id] ? (
                              <CheckSquare className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {quote.speaker}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {quote.date || quote.quote_date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {quote.rating}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 line-clamp-2">
                          {quote.quote_text}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handlePreview(quote)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Preview
                          </button>
                          
                          {status === STATUS_OPTIONS.New && (
                            <>
                              <button
                                onClick={() => handleSendToClaude(quote)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Send to Claude
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(quote.id, STATUS_OPTIONS.Discarded)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Discard
                              </button>
                            </>
                          )}
                          
                          {status === STATUS_OPTIONS.Processing && (
                            <button
                              onClick={() => handleUpdateStatus(quote.id, STATUS_OPTIONS.New)}
                              className="text-amber-600 hover:text-amber-900"
                            >
                              Back to New
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
      
      {/* Preview Modal */}
      <Modal 
        isOpen={showPreview && selectedQuote !== null} 
        onClose={handleCloseModal} 
        title="Quote Preview"
      >
        {selectedQuote && (
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-2">{selectedQuote.speaker}</h3>
            <p className="text-sm text-gray-500 mb-4">{selectedQuote.date || selectedQuote.quote_date}</p>
            
            <div className="mb-4">
              <p className="mb-1 font-medium">Quote:</p>
              <p className="p-2 bg-gray-100 rounded">{selectedQuote.quote_text}</p>
            </div>
            
            {selectedQuote.article_title && (
              <div className="mb-4">
                <p className="mb-1 font-medium">Article Title:</p>
                <p className="p-2 bg-gray-100 rounded">{selectedQuote.article_title}</p>
              </div>
            )}
            
            {selectedQuote.full_analysis && (
              <div className="mb-4">
                <p className="mb-1 font-medium">Full PolitiFact Analysis:</p>
                <div className="p-2 bg-gray-100 rounded max-h-40 overflow-y-auto">
                  <p className="text-sm">{selectedQuote.full_analysis}</p>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <p className="mb-1 font-medium">Rating:</p>
              <p className="p-2 bg-gray-100 rounded">{selectedQuote.rating || 'No rating'}</p>
            </div>
            
            <div className="mb-4">
              <p className="mb-1 font-medium">Source:</p>
              <a 
                href={selectedQuote.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {selectedQuote.source_url}
              </a>
            </div>
            
            {/* Display additional source links if available */}
            {selectedQuote.source_links && (
              <div className="mb-4">
                <p className="mb-1 font-medium">Additional Sources:</p>
                <div className="p-2 bg-gray-100 rounded">
                  {(() => {
                    let links = [];
                    try {
                      links = JSON.parse(selectedQuote.source_links);
                    } catch (e) {
                      console.warn('Error parsing source links:', e);
                    }
                    
                    if (Array.isArray(links) && links.length > 0) {
                      return (
                        <ul className="list-disc pl-5">
                          {links.map((link, index) => (
                            <li key={index} className="mb-1">
                              <a 
                                href={typeof link === 'object' ? link.url : link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-sm break-all"
                              >
                                {typeof link === 'object' ? link.url : link}
                              </a>
                              {typeof link === 'object' && link.text && (
                                <p className="text-xs text-gray-600 mt-1">{link.text}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      );
                    } else {
                      return <p className="text-gray-600">No additional sources available</p>;
                    }
                  })()}
                </div>
              </div>
            )}
            
            {/* Show Raw Data section - expandable */}
            <div className="mt-5 border-t pt-3">
              <details>
                <summary className="font-medium text-blue-600 cursor-pointer">
                  Show All Data Fields (Raw Data)
                </summary>
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono overflow-x-auto">
                  <pre>{JSON.stringify(selectedQuote, null, 2)}</pre>
                </div>
              </details>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Claude Analysis Modal */}
      <Modal 
        isOpen={showClaudeForm && selectedQuote !== null} 
        onClose={handleCloseModal} 
        title="Analyze with Claude" 
      >
        {selectedQuote && (
          <ClaudeAnalysisForm
            quote={selectedQuote}
            onAnalysisComplete={handleClaudeAnalysisComplete}
            onCancel={handleCloseModal}
            existingTags={existingTags}
          />
        )}
      </Modal>
    </div>
  );
};

export default PolitiFactScraper;